import type { Node, Edge, Connection } from '@xyflow/react';
import type {
    WorkflowNodeData,
    StateNodeData,
    ParallelGatewayNodeData,
    MergeNodeData,
    AslStateType,
    WorkflowNodeType,
    ValidationIssue,
} from '@/types/workflow-graph';

// =============================================================================
// ASL Connection Rules
// =============================================================================

/**
 * ASL connection rules enforced by this validator:
 * 
 * 1. OUTGOING EDGE CARDINALITY:
 *    - Task: N outgoing (dynamic routing via JSONPath Next + NextTargets)
 *    - Pass, Wait: max 1 outgoing (Next)
 *    - Parallel, Map: max 1 outgoing (after join/completion)
 *    - Choice: N outgoing (rules + default)
 *    - Succeed, Fail: 0 outgoing (terminal)
 * 
 * 2. CONTROL FLOW RULES:
 *    - Parallel fork must have corresponding join
 *    - Merge is valid ONLY for mutually exclusive paths
 *    - Cannot merge concurrent branches (must use Parallel join)
 * 
 * 3. GENERAL RULES:
 *    - No self-loops
 *    - No duplicate connections
 *    - No cycles (in most cases)
 */

// States that can have outgoing connections via Next (single)
const STATES_WITH_SINGLE_NEXT: AslStateType[] = ['Pass', 'Wait', 'Parallel', 'Map'];
// Task nodes support dynamic routing: multiple outgoing edges resolved at runtime
const STATES_WITH_DYNAMIC_NEXT: AslStateType[] = ['Task'];
const STATES_WITH_BRANCHES: AslStateType[] = ['Choice'];
const TERMINAL_STATES: AslStateType[] = ['Succeed', 'Fail'];

// Maximum outgoing connections per state type
const MAX_OUTGOING: Record<AslStateType, number> = {
    Task: 20,    // Dynamic routing via JSONPath Next + NextTargets
    Pass: 1,
    Wait: 1,
    Parallel: 1, // After all branches complete
    Map: 1,      // After iteration completes
    Choice: 20,  // Multiple branches + default
    Succeed: 0,
    Fail: 0,
};

// UI-only node types that have special connection rules
const CONTROL_FLOW_NODE_TYPES: WorkflowNodeType[] = ['parallelFork', 'parallelJoin', 'merge'];
const MARKER_NODE_TYPES: WorkflowNodeType[] = ['startMarker'];

// =============================================================================
// Connection Validation Result Types
// =============================================================================

export interface ConnectionValidationResult {
    valid: boolean;
    reason?: string;
    /** Suggested action if connection is invalid but recoverable */
    suggestedAction?: 'use_choice' | 'use_parallel';
    /** Severity of the validation issue */
    severity?: 'error' | 'warning';
}

export interface GraphValidationResult {
    valid: boolean;
    canExport: boolean;
    issues: ValidationIssue[];
}

// =============================================================================
// Connection Validation
// =============================================================================

/**
 * Validate if a connection between two nodes is legal according to ASL rules.
 */
export function validateConnection(
    connection: Connection,
    nodes: Node<WorkflowNodeData>[],
    edges: { source: string; target: string; id?: string }[]
): ConnectionValidationResult {
    const sourceNode = nodes.find((n) => n.id === connection.source);
    const targetNode = nodes.find((n) => n.id === connection.target);

    if (!sourceNode || !targetNode) {
        return { valid: false, reason: 'Source or target node not found', severity: 'error' };
    }

    // Get node types
    const sourceNodeType = getNodeType(sourceNode);
    const targetNodeType = getNodeType(targetNode);

    // Rule 0: Cannot connect to itself
    if (connection.source === connection.target) {
        return { valid: false, reason: 'Cannot connect a state to itself', severity: 'error' };
    }

    // Rule 1: Terminal states cannot have outgoing connections
    if (sourceNodeType === 'Succeed' || sourceNodeType === 'Fail') {
        return {
            valid: false,
            reason: `${sourceNodeType} is a terminal state and cannot have outgoing connections`,
            severity: 'error',
        };
    }

    // Rule 2: Start markers can only connect to one state
    if (sourceNodeType === 'startMarker') {
        const existingFromStart = edges.filter((e) => e.source === connection.source).length;
        if (existingFromStart > 0) {
            return {
                valid: false,
                reason: 'Start marker can only connect to one state (StartAt)',
                severity: 'error',
            };
        }
    }

    // Rule 4: Prevent duplicate connections
    const duplicateExists = edges.some(
        (e) => e.source === connection.source && e.target === connection.target
    );
    if (duplicateExists) {
        return { valid: false, reason: 'Connection already exists', severity: 'warning' };
    }

    // Handle ASL state nodes
    if (isAslStateType(sourceNodeType)) {
        return validateAslStateConnection(
            connection,
            sourceNode,
            targetNode,
            sourceNodeType,
            edges
        );
    }

    // Handle control flow nodes
    if (CONTROL_FLOW_NODE_TYPES.includes(sourceNodeType as WorkflowNodeType)) {
        return validateControlFlowConnection(connection, sourceNode, targetNode, edges);
    }

    return { valid: true };
}

/**
 * Validate connection from an ASL state node.
 */
function validateAslStateConnection(
    connection: Connection,
    sourceNode: Node<WorkflowNodeData>,
    targetNode: Node<WorkflowNodeData>,
    sourceType: AslStateType,
    edges: { source: string; target: string }[]
): ConnectionValidationResult {
    const existingOutgoing = edges.filter((e) => e.source === connection.source).length;
    const maxAllowed = MAX_OUTGOING[sourceType];

    // Choice states can have multiple outgoing connections
    if (STATES_WITH_BRANCHES.includes(sourceType)) {
        if (existingOutgoing >= maxAllowed) {
            return {
                valid: false,
                reason: `${sourceType} has too many branches (max ${maxAllowed})`,
                severity: 'warning',
            };
        }
        return { valid: true };
    }

    // Task nodes support dynamic routing (multiple outgoing edges)
    if (STATES_WITH_DYNAMIC_NEXT.includes(sourceType)) {
        if (existingOutgoing >= maxAllowed) {
            return {
                valid: false,
                reason: `${sourceType} has too many dynamic targets (max ${maxAllowed})`,
                severity: 'warning',
            };
        }
        return { valid: true };
    }

    // Other standard states (Pass, Wait, Parallel, Map) can only have one Next
    if (STATES_WITH_SINGLE_NEXT.includes(sourceType)) {
        if (existingOutgoing > 0) {
            return {
                valid: false,
                reason: `${sourceType} can only have one 'Next' connection. Use a Choice or Parallel node for branching.`,
                severity: 'warning',
                suggestedAction: 'use_choice',
            };
        }
    }

    // Check max outgoing connections
    if (existingOutgoing >= maxAllowed) {
        return {
            valid: false,
            reason: `${sourceType} already has maximum outgoing connections (${maxAllowed})`,
            severity: 'error',
        };
    }

    return { valid: true };
}

/**
 * Validate connection from control flow nodes.
 */
function validateControlFlowConnection(
    connection: Connection,
    sourceNode: Node<WorkflowNodeData>,
    targetNode: Node<WorkflowNodeData>,
    edges: { source: string; target: string }[]
): ConnectionValidationResult {
    const sourceData = sourceNode.data as WorkflowNodeData;

    // Parallel join can only have one outgoing connection
    if (sourceData.nodeType === 'parallelJoin') {
        const existingOutgoing = edges.filter((e) => e.source === connection.source).length;
        if (existingOutgoing > 0) {
            return {
                valid: false,
                reason: 'Parallel join can only have one outgoing connection (to Next state)',
                severity: 'error',
            };
        }
    }

    // Merge nodes can only have one outgoing connection
    if (sourceData.nodeType === 'merge') {
        const existingOutgoing = edges.filter((e) => e.source === connection.source).length;
        if (existingOutgoing > 0) {
            return {
                valid: false,
                reason: 'Merge node can only have one outgoing connection',
                severity: 'error',
            };
        }
    }

    return { valid: true };
}

// =============================================================================
// Graph-Level Validation
// =============================================================================

/**
 * Validate the entire graph for export/run readiness.
 */
export function validateGraph(
    nodes: Node<WorkflowNodeData>[],
    edges: Edge[]
): GraphValidationResult {
    const issues: ValidationIssue[] = [];

    // Check for invalid merges (concurrent paths converging)
    const mergeNodes = nodes.filter((n) => (n.data as WorkflowNodeData).nodeType === 'merge');
    for (const merge of mergeNodes) {
        const mergeData = merge.data as MergeNodeData;
        if (mergeData.validation === 'invalid') {
            issues.push({
                type: 'invalid_merge',
                severity: 'error',
                message: mergeData.validationMessage || 'Invalid merge: concurrent paths cannot use generic merge',
                nodeIds: [merge.id],
                autoFix: {
                    type: 'add_join',
                    description: 'Convert to Parallel join',
                },
            });
        }
    }

    // Check for orphan nodes (no incoming or outgoing connections)
    for (const node of nodes) {
        const data = node.data as WorkflowNodeData;
        
        // Skip markers
        if (data.nodeType === 'startMarker') continue;

        const hasIncoming = edges.some((e) => e.target === node.id);
        const hasOutgoing = edges.some((e) => e.source === node.id);

        // Check for states that should have incoming (not start)
        if (!data.isStart && !hasIncoming && data.nodeType === 'state') {
            issues.push({
                type: 'orphan_node',
                severity: 'warning',
                message: `State "${data.label}" has no incoming connections`,
                nodeIds: [node.id],
            });
        }

        // Note: States without outgoing edges are valid - they will be
        // automatically marked as End:true during ASL export
    }

    // Check for parallel fork without corresponding join
    const parallelForks = nodes.filter((n) => (n.data as WorkflowNodeData).nodeType === 'parallelFork');
    for (const fork of parallelForks) {
        const forkData = fork.data as ParallelGatewayNodeData;
        const pairedJoin = nodes.find((n) => n.id === forkData.pairedNodeId);
        if (!pairedJoin) {
            issues.push({
                type: 'missing_join',
                severity: 'error',
                message: 'Parallel fork requires a corresponding join',
                nodeIds: [fork.id],
                autoFix: {
                    type: 'add_join',
                    description: 'Add parallel join node',
                },
            });
        }
    }

    // Check for missing Default branch on Choice states
    const choiceNodes = nodes.filter((n) => {
        const data = n.data as WorkflowNodeData;
        return data.nodeType === 'state' && (data as StateNodeData).stateType === 'Choice';
    });
    for (const choice of choiceNodes) {
        const stateData = (choice.data as StateNodeData).state;
        if (!stateData.Default) {
            issues.push({
                type: 'missing_default_branch',
                severity: 'warning',
                message: `Choice state "${(choice.data as StateNodeData).label}" has no Default branch`,
                nodeIds: [choice.id],
                autoFix: {
                    type: 'add_default',
                    description: 'Add Default branch',
                },
            });
        }
    }

    const hasErrors = issues.some((i) => i.severity === 'error');

    return {
        valid: issues.length === 0,
        canExport: !hasErrors,
        issues,
    };
}

// =============================================================================
// Merge Validation (Fan-in)
// =============================================================================

/**
 * Validate if a merge node is valid.
 * 
 * CRITICAL: Fan-in is ONLY for structural convergence of mutually exclusive paths.
 * It MUST NOT be used for synchronization of concurrent paths.
 */
export function validateMerge(
    mergeNodeId: string,
    nodes: Node<WorkflowNodeData>[],
    edges: Edge[]
): { validation: 'valid' | 'warning' | 'invalid'; message?: string } {
    // Find all incoming edges to this merge
    const incomingEdges = edges.filter((e) => e.target === mergeNodeId);

    if (incomingEdges.length <= 1) {
        return { validation: 'valid' };
    }

    // For each incoming edge, trace back to find the divergence point
    const sourceNodes = incomingEdges.map((e) =>
        nodes.find((n) => n.id === e.source)
    ).filter(Boolean) as Node<WorkflowNodeData>[];

    // Check if any sources come from a Parallel fork (concurrent paths)
    const parallelSources = sourceNodes.filter((n) => {
        // Check if this node is within a parallel branch
        return isInParallelBranch(n.id, nodes, edges);
    });

    if (parallelSources.length > 0) {
        return {
            validation: 'invalid',
            message: 'Cannot merge concurrent parallel branches. Use Parallel join instead.',
        };
    }

    // Check if sources come from the same Choice (XOR - mutually exclusive)
    const choiceSources = traceBackToChoice(sourceNodes, nodes, edges);
    if (choiceSources.allFromSameChoice) {
        return { validation: 'valid' };
    }

    // Uncertain - warn user to verify
    return {
        validation: 'warning',
        message: 'Verify these paths are mutually exclusive (only one can be taken).',
    };
}

/**
 * Check if a node is within a parallel branch.
 */
function isInParallelBranch(
    nodeId: string,
    nodes: Node<WorkflowNodeData>[],
    edges: Edge[]
): boolean {
    // Simple check: trace back and see if we encounter a parallelFork
    const visited = new Set<string>();
    const queue = [nodeId];

    while (queue.length > 0) {
        const currentId = queue.shift()!;
        if (visited.has(currentId)) continue;
        visited.add(currentId);

        const node = nodes.find((n) => n.id === currentId);
        if (!node) continue;

        const data = node.data as WorkflowNodeData;
        if (data.nodeType === 'parallelFork') {
            return true;
        }

        // Add upstream nodes
        const incomingEdges = edges.filter((e) => e.target === currentId);
        for (const edge of incomingEdges) {
            queue.push(edge.source);
        }
    }

    return false;
}

/**
 * Trace back from nodes to find if they all originate from the same Choice.
 */
function traceBackToChoice(
    sourceNodes: Node<WorkflowNodeData>[],
    nodes: Node<WorkflowNodeData>[],
    edges: Edge[]
): { allFromSameChoice: boolean; choiceNodeId?: string } {
    const choiceOrigins = new Set<string>();

    for (const source of sourceNodes) {
        const choiceId = findUpstreamChoice(source.id, nodes, edges);
        if (choiceId) {
            choiceOrigins.add(choiceId);
        }
    }

    if (choiceOrigins.size === 1) {
        return { allFromSameChoice: true, choiceNodeId: Array.from(choiceOrigins)[0] };
    }

    return { allFromSameChoice: false };
}

/**
 * Find the nearest upstream Choice node.
 */
function findUpstreamChoice(
    nodeId: string,
    nodes: Node<WorkflowNodeData>[],
    edges: Edge[]
): string | null {
    const visited = new Set<string>();
    const queue = [nodeId];

    while (queue.length > 0) {
        const currentId = queue.shift()!;
        if (visited.has(currentId)) continue;
        visited.add(currentId);

        const node = nodes.find((n) => n.id === currentId);
        if (!node) continue;

        const data = node.data as WorkflowNodeData;
        if (data.nodeType === 'state' && (data as StateNodeData).stateType === 'Choice') {
            return currentId;
        }

        // Add upstream nodes
        const incomingEdges = edges.filter((e) => e.target === currentId);
        for (const edge of incomingEdges) {
            queue.push(edge.source);
        }
    }

    return null;
}

// =============================================================================
// Cycle Detection
// =============================================================================

/**
 * Check if connecting would create a cycle (DFS).
 */
export function wouldCreateCycle(
    connection: Connection,
    edges: { source: string; target: string }[]
): boolean {
    const visited = new Set<string>();

    const dfs = (nodeId: string): boolean => {
        if (nodeId === connection.source) return true;
        if (visited.has(nodeId)) return false;

        visited.add(nodeId);

        for (const edge of edges) {
            if (edge.source === nodeId) {
                if (dfs(edge.target)) return true;
            }
        }

        return false;
    };

    // Start from target and see if we can reach source
    return dfs(connection.target!);
}

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Get the effective node type from a node.
 */
function getNodeType(node: Node<WorkflowNodeData>): WorkflowNodeType {
    const data = node.data as WorkflowNodeData;

    if (data.nodeType === 'state') {
        return (data as StateNodeData).stateType;
    }

    return data.nodeType as WorkflowNodeType;
}

/**
 * Check if a type is an ASL state type.
 */
function isAslStateType(type: WorkflowNodeType): type is AslStateType {
    return ['Task', 'Pass', 'Wait', 'Choice', 'Parallel', 'Map', 'Succeed', 'Fail'].includes(type);
}

/**
 * Get valid handle types for a node based on its type.
 */
export function getNodeHandles(nodeType: WorkflowNodeType): {
    hasInput: boolean;
    hasOutput: boolean;
    outputHandles: string[];
} {
    const hasInput = !['startMarker'].includes(nodeType);

    if (TERMINAL_STATES.includes(nodeType as AslStateType)) {
        return { hasInput, hasOutput: false, outputHandles: [] };
    }

    if (nodeType === 'Choice') {
        return {
            hasInput,
            hasOutput: true,
            outputHandles: ['default', 'choice_0', 'choice_1', 'choice_2', 'choice_3'],
        };
    }

    if (nodeType === 'parallelFork') {
        return {
            hasInput,
            hasOutput: true,
            outputHandles: ['branch-0', 'branch-1', 'branch-2', 'branch-3'],
        };
    }

    return { hasInput, hasOutput: true, outputHandles: ['next'] };
}

